import React, { useEffect, useState, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../api";
import { AuthContext } from "../AuthContext";

export default function MekFundedClaim() {
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const navigate = useNavigate();

    const { token: authToken } = useContext(AuthContext);

    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [claiming, setClaiming] = useState(false);

    useEffect(() => {
        async function loadClaim() {
            try {
                const res = await fetch(
                    `${API_BASE_URL}/api/mekfunded/claim/${token}`
                );

                const data = await res.json();

                if (!res.ok) {
                    throw new Error(
                        data.message || "Invalid claim token"
                    );
                }

                setAccount(data.account);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        if (!token) {
            setError("Missing claim token");
            setLoading(false);
            return;
        }

        loadClaim();
    }, [token]);

    async function claimAccount() {
        if (!authToken) {
            navigate(
                `/login?redirect=/claim/mekfunded?token=${token}`
            );
            return;
        }

        try {
            setClaiming(true);

            const res = await fetch(
                `${API_BASE_URL}/api/mekfunded/claim-complete/${token}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            const data = await res.json();

            if (!res.ok) {
                throw new Error(
                    data.message || "Claim failed"
                );
            }

            navigate("/app");
        } catch (err) {
            setError(err.message);
        } finally {
            setClaiming(false);
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                Checking your MekFunded account...
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="rounded-3xl bg-red-50 p-8">
                    <h2 className="text-xl font-bold">
                        Claim Failed
                    </h2>

                    <p className="mt-3">
                        {error}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-white to-slate-50 flex items-center justify-center px-5">
            <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                <h1 className="text-3xl font-bold text-indigo-700">
                    Connect MekFunded Account
                </h1>

                <p className="mt-3 text-gray-600">
                    Your funded account is ready to connect with MekEdge.
                </p>

                <div className="mt-6 space-y-4 bg-slate-50 rounded-2xl p-5">
                    <Info
                        label="Account ID"
                        value={account?.fundedAccountId}
                    />

                    <Info
                        label="Phase"
                        value={account?.phase}
                    />

                    <Info
                        label="MT5 Login"
                        value={account?.mt5Login}
                    />

                    <Info
                        label="Server"
                        value={account?.mt5Server}
                    />

                    <Info
                        label="Broker"
                        value={account?.broker}
                    />
                </div>

                <button
                    onClick={claimAccount}
                    disabled={claiming}
                    className="mt-8 w-full rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white py-3 font-semibold shadow-sm disabled:opacity-50"
                >
                    {claiming
                        ? "Connecting..."
                        : "Connect to MekEdge"}
                </button>
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <p className="text-xs text-gray-500">
                {label}
            </p>

            <p className="font-semibold">
                {value || "-"}
            </p>
        </div>
    );
}